import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import Labrador from '../components/Labrador';

export default function AuthPage({ initialMode, onBack }) {
  const { login, completeMfa, register } = useAuth();
  const [mode, setMode]         = useState(initialMode || 'login'); // 'login' | 'register'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
  const [busy, setBusy]         = useState(false);
  const [mfaToken, setMfaToken] = useState(null); // set → show the code step
  const [code, setCode]         = useState('');
  const [pendingEmail, setPendingEmail]   = useState(null); // set after register → "check your email"
  const [unverifiedEmail, setUnverifiedEmail] = useState(null); // set on 403 login → offer resend
  const [resendStatus, setResendStatus]   = useState(''); // '' | 'sending' | 'sent'
  const [verifyResult, setVerifyResult]   = useState(null); // result of an auto-verify from an emailed link

  // If this page was opened from the emailed verification link (?verify=TOKEN),
  // verify automatically and show the result, then strip the token from the URL.
  // Guarded with a ref so React StrictMode's dev-only double-invoke can't fire
  // the (single-use) verify call twice and clobber a real success with the
  // second call's "already used" result.
  const verifyStarted = useRef(false);
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('verify');
    if (!token || verifyStarted.current) return;
    verifyStarted.current = true;
    authApi.verifyEmail(token)
      .then(({ data }) => setVerifyResult(data))
      .catch(() => setVerifyResult({ verified: false, message: 'Something went wrong verifying that link.' }))
      .finally(() => window.history.replaceState({}, '', window.location.pathname));
  }, []);

  const isRegister = mode === 'register';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (isRegister && password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      if (isRegister) {
        await register(email, password);
        setPendingEmail(email);
      } else {
        setUnverifiedEmail(null);
        setResendStatus('');
        const res = await login(email, password);
        if (res.mfaRequired) { setMfaToken(res.mfaToken); setCode(''); }
      }
    } catch (err) {
      const status = err.response?.status;
      const serverMsg = err.response?.data?.error;
      if (status === 401)      setError('Invalid email or password.');
      else if (status === 403) { setError(serverMsg || 'Please verify your email before signing in.'); setUnverifiedEmail(email); }
      else if (status === 409) setError('An account with that email already exists.');
      else if (status === 400) setError(serverMsg || 'Password must be at least 8 characters with a letter and a digit.');
      else if (status === 429) setError('Too many attempts. Please wait a minute and try again.');
      else                     setError('Something went wrong — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setResendStatus('sending');
    try {
      await authApi.resendVerify(unverifiedEmail);
    } catch { /* uniform response regardless — nothing to special-case */ }
    setResendStatus('sent');
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await completeMfa(mfaToken, code.trim());
    } catch (err) {
      const status = err.response?.status;
      if (status === 401)      setError(err.response?.data?.error || 'Invalid or expired code.');
      else if (status === 429) setError('Too many attempts. Please wait a minute and try again.');
      else                     setError('Something went wrong — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const cancelMfa = () => { setMfaToken(null); setCode(''); setError(''); setPassword(''); };

  if (mfaToken) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div className="card" style={{ width: '100%', maxWidth: 380, padding: '32px 28px' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <Labrador size={44} style={{ color: 'var(--blue)', marginBottom: 10 }} />
            <div className="brand" style={{ fontSize: 22 }}>TRIGGER BUDGET</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>Two-factor verification</div>
          </div>

          <form onSubmit={handleMfaSubmit}>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Authentication code</label>
              <input className="field-input" type="text" inputMode="numeric" autoFocus autoComplete="one-time-code"
                placeholder="6-digit code or recovery code"
                style={{ width: '100%', letterSpacing: 1 }} value={code}
                onChange={e => setCode(e.target.value)} />
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                Open your authenticator app, or enter one of your recovery codes.
              </div>
            </div>

            {error && (
              <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', color: 'var(--red)',
                borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 14 }}>
                {error}
              </div>
            )}

            <button className="btn btn-primary" type="submit" disabled={busy || !code.trim()}
              style={{ width: '100%', padding: '10px 0', fontSize: 14 }}>
              {busy ? 'Verifying…' : 'Verify'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 18, fontSize: 12, color: 'var(--text2)' }}>
            <button type="button" onClick={cancelMfa}
              style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer',
                fontSize: 12, fontWeight: 700, padding: 0 }}>
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (verifyResult) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div className="card" style={{ width: '100%', maxWidth: 380, padding: '32px 28px', textAlign: 'center' }}>
          <Labrador size={44} style={{ color: 'var(--blue)', marginBottom: 10 }} />
          <div className="brand" style={{ fontSize: 22, marginBottom: 16 }}>TRIGGER BUDGET</div>
          <div style={{
            background: verifyResult.verified ? 'var(--green-bg)' : 'var(--red-bg)',
            border: `1px solid ${verifyResult.verified ? 'var(--green)' : 'var(--red)'}`,
            color: verifyResult.verified ? 'var(--green)' : 'var(--red)',
            borderRadius: 8, padding: '12px 14px', fontSize: 13, marginBottom: 18,
          }}>
            {verifyResult.message}
          </div>
          <button className="btn btn-primary" style={{ width: '100%', padding: '10px 0', fontSize: 14 }}
            onClick={() => { setVerifyResult(null); setMode('login'); }}>
            Continue to sign in
          </button>
        </div>
      </div>
    );
  }

  if (pendingEmail) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div className="card" style={{ width: '100%', maxWidth: 380, padding: '32px 28px', textAlign: 'center' }}>
          <Labrador size={44} style={{ color: 'var(--blue)', marginBottom: 10 }} />
          <div className="brand" style={{ fontSize: 22, marginBottom: 16 }}>TRIGGER BUDGET</div>
          <div style={{ fontSize: 14, color: 'var(--text)', marginBottom: 8, fontWeight: 600 }}>Check your email</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 18 }}>
            We sent a verification link to <strong>{pendingEmail}</strong>. Click it to activate your account, then come back and sign in.
          </div>
          <button className="btn" style={{ width: '100%', padding: '10px 0', fontSize: 14, marginBottom: 10 }}
            disabled={resendStatus !== ''}
            onClick={async () => { setResendStatus('sending'); try { await authApi.resendVerify(pendingEmail); } catch {} setResendStatus('sent'); }}>
            {resendStatus === 'sent' ? 'Link resent' : resendStatus === 'sending' ? 'Sending…' : "Didn't get it? Resend"}
          </button>
          <button type="button" onClick={() => { setPendingEmail(null); setMode('login'); }}
            style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer',
              fontSize: 12, fontWeight: 700, padding: 0 }}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="card" style={{ width: '100%', maxWidth: 380, padding: '32px 28px' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Labrador size={44} style={{ color: 'var(--blue)', marginBottom: 10 }} />
          <div className="brand" style={{ fontSize: 22 }}>TRIGGER BUDGET</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>Run Wild. Spend Smart.</div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>Email</label>
            <input className="field-input" type="email" required autoComplete="email"
              style={{ width: '100%' }} value={email}
              onChange={e => setEmail(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>Password</label>
            <input className="field-input" type="password" required
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              style={{ width: '100%' }} value={password}
              onChange={e => setPassword(e.target.value)} />
            {isRegister && (
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                At least 8 characters, with a letter and a digit.
              </div>
            )}
          </div>
          {isRegister && (
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Confirm password</label>
              <input className="field-input" type="password" required autoComplete="new-password"
                style={{ width: '100%' }} value={confirm}
                onChange={e => setConfirm(e.target.value)} />
            </div>
          )}

          {error && (
            <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', color: 'var(--red)',
              borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 14 }}>
              {error}
              {unverifiedEmail && (
                <div style={{ marginTop: 8 }}>
                  <button type="button" disabled={resendStatus !== ''} onClick={handleResend}
                    style={{ background: 'none', border: 'none', color: 'var(--red)', textDecoration: 'underline',
                      cursor: 'pointer', fontSize: 12, fontWeight: 700, padding: 0 }}>
                    {resendStatus === 'sent' ? 'Link resent — check your inbox' : resendStatus === 'sending' ? 'Sending…' : 'Resend verification email'}
                  </button>
                </div>
              )}
            </div>
          )}

          <button className="btn btn-primary" type="submit" disabled={busy}
            style={{ width: '100%', padding: '10px 0', fontSize: 14 }}>
            {busy ? 'Please wait…' : isRegister ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 18, fontSize: 12, color: 'var(--text2)' }}>
          {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button type="button"
            onClick={() => { setMode(isRegister ? 'login' : 'register'); setError(''); }}
            style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer',
              fontSize: 12, fontWeight: 700, padding: 0 }}>
            {isRegister ? 'Sign in' : 'Create one'}
          </button>
        </div>
        {onBack && (
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <button type="button" onClick={onBack}
              style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer',
                fontSize: 11, padding: 0 }}>
              ‹ Back to home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
